// WijkImpact.js, het venster "Wijk impact van de laadpleinen" op tabblad
// Laadpleinen. Gebouwd naar mockup Wijk impact V8 (akkoord HL 27 oktober 2026).
//
// Wat het toont, per wijk en per plein, volgens de regel van HL:
//   A. een plein vangt eerst de nieuwe laadpunten van de eigen wijk op, vanaf
//      het jaar van plaatsing, jaar na jaar;
//   B. wat daarna nog over is, neemt losse laadpunten over in andere wijken
//      binnen de loopafstand, gepland na het jaar van plaatsing.
// Het rekenen zelf gebeurt in rasterdekking.js (plaatsPalen, pleinEigenWijk,
// pleinAndereWijken). Dit venster leest die uitkomst en rekent alleen de
// voorvertoning van een kandidaat die nog niet gekozen is.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { pleinEigenWijk, pleinAndereWijken, wijkVanPunt } from '../rasterdekking';
import { PlanBlok } from '../stroomnet';

const WK = { geen: '#E5E7EB', deel: '#CFE8EC', alles: '#8FC9D3' };
const WN = {
  geen: 'geen laadpunten via laadpleinen',
  deel: 'een deel van de nieuwe laadpunten via laadpleinen',
  alles: 'alle nieuwe laadpunten via laadpleinen',
};
const ORANJE = '#D9480F';
const AFSTANDEN = [250, 500, 750];

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('nl-BE');

export default function WijkImpact({
  open, onClose, kaartAdres, wijken = [], sectorenGeo = null, raster = null,
  jaren = [], startJaar, jaardeel = {}, plaatsResultaat = null,
  gekozen = [], kandidaten = [], pleinKleur = {}, pleinOmvang = 12,
  loopM = 250, zetLoopM, onKies, planVoor = null, planMeta = null,
  beginJaar = null, eindJaar = null,
}) {
  const kaartDiv = useRef(null);
  const kaart = useRef(null);
  const lagen = useRef(null);
  const sectorLaag = useRef(null);
  const zoomNaWijziging = useRef(false);
  const [peiljaar, setPeiljaar] = useState(startJaar);
  const [tab, setTab] = useState('wijk');
  const [actief, setActief] = useState(null);          // id van een locatie
  const [gekozenWijk, setGekozenWijk] = useState(null);

  useEffect(() => { if (open) setPeiljaar(startJaar); }, [open, startJaar]);

  const wijkNaam = useMemo(() => {
    const m = {};
    for (const w of wijken) m[w.id] = w.naam;
    return m;
  }, [wijken]);

  const impact = plaatsResultaat?.pleinImpact || null;
  // De grens van de berekening: het laatste jaar van de planperiode uit
  // Parameters (besluit HL 28 oktober 2026). Zelfde grens als in plaatsPalen.
  const laatsteJaar = impact?.eindJaar ?? eindJaar ?? jaren[jaren.length - 1];
  const jarenTot = useMemo(() => jaren.filter(j => j <= laatsteJaar), [jaren, laatsteJaar]);

  // Alle losse palen van de plaatsing, ook de palen die een plein overneemt.
  // Nodig om bij een kandidaat opnieuw te kunnen bepalen wat hij zou overnemen.
  const alleLosse = useMemo(() => {
    const uit = [];
    for (const j of jaren) {
      const pj = plaatsResultaat?.perJaar?.[j];
      if (!pj) continue;
      for (const p of pj.plekken || []) if (p.soort !== 'plein') uit.push(p);
      for (const p of pj.vervallen || []) uit.push(p);
    }
    return uit;
  }, [plaatsResultaat, jaren]);

  const gekozenIds = useMemo(() => new Set(gekozen.map(l => String(l.id))), [gekozen]);
  const actiefLoc = actief != null
    ? (gekozen.find(l => String(l.id) === String(actief)) || kandidaten.find(k => String(k.id) === String(actief)) || null)
    : null;
  const actiefIsKandidaat = !!(actiefLoc && !gekozenIds.has(String(actiefLoc.id)));

  // De voorvertoning: dezelfde regel met de kandidaat erbij.
  const voorvertoning = useMemo(() => {
    if (!impact || !actiefIsKandidaat || !raster) return null;
    const wijkId = wijkVanPunt(raster, actiefLoc.lat, actiefLoc.lng);
    if (!wijkId) return null;
    const jaar = actiefLoc.jaar != null && jaren.includes(Number(actiefLoc.jaar)) ? Number(actiefLoc.jaar) : jaren[0];
    const punten = Math.min(pleinOmvang, actiefLoc.vakken ?? pleinOmvang);
    const pleinen = [...(impact.pleinen || []),
      { id: actiefLoc.id, naam: actiefLoc.naam, lat: actiefLoc.lat, lng: actiefLoc.lng, punten, jaar, wijkId }];
    const a = pleinEigenWijk(jarenTot, jaardeel, pleinen);
    const b = pleinAndereWijken(pleinen, a, alleLosse, loopM, laatsteJaar);
    return { pleinen, ...a, ...b };
  }, [impact, actiefIsKandidaat, actiefLoc, raster, jaren, jarenTot, laatsteJaar, jaardeel, alleLosse, loopM, pleinOmvang]);

  const getoond = voorvertoning || impact;

  // Per wijk: nieuwe laadpunten tot het peiljaar, waarvan via laadpleinen.
  const rijen = useMemo(() => {
    if (!impact) return [];
    const via = {};
    // Wat de pleinen in de eigen wijk opvangen, tot en met het eindjaar en
    // daarna, zodat de tabel ook klopt bij een peiljaar na het eindjaar.
    for (const bron of [impact.eigen || {}, impact.eigenNa || {}]) {
      for (const [wid, perJaar] of Object.entries(bron)) {
        for (const [j, n] of Object.entries(perJaar)) if (Number(j) <= peiljaar) via[wid] = (via[wid] || 0) + n;
      }
    }
    for (const [p] of (impact.overgenomen || new Map())) {
      if (p.jaar <= peiljaar) via[p.wijkId] = (via[p.wijkId] || 0) + p.punten;
    }
    return wijken.map(w => {
      const n = jaren.filter(j => j <= peiljaar).reduce((s, j) => s + Math.round(jaardeel?.[w.id]?.[j] || 0), 0);
      const v = Math.min(n, via[w.id] || 0);
      return { id: w.id, naam: w.naam, n, v, los: n - v, st: !v ? 'geen' : v >= n ? 'alles' : 'deel' };
    }).sort((a, b) => b.n - a.n);
  }, [impact, wijken, jaren, jaardeel, peiljaar]);
  const statusWijk = useMemo(() => {
    const m = {};
    for (const r of rijen) m[r.id] = r.st;
    return m;
  }, [rijen]);

  // ── De kaart ──
  useEffect(() => {
    if (!open || !kaartDiv.current || kaart.current) return;
    const m = L.map(kaartDiv.current, { zoomSnap: 0.25, preferCanvas: true });
    L.tileLayer(kaartAdres, { maxZoom: 20, attribution: '© OpenStreetMap © CARTO' }).addTo(m);
    m.createPane('wi_wijk').style.zIndex = 350;
    m.createPane('wi_cirkel').style.zIndex = 380;
    m.createPane('wi_los').style.zIndex = 420;
    m.createPane('wi_kand').style.zIndex = 450;
    kaart.current = m;
    lagen.current = L.layerGroup().addTo(m);
    return undefined;
  }, [open, kaartAdres]);

  useEffect(() => () => { if (kaart.current) { kaart.current.remove(); kaart.current = null; } }, []);
  useEffect(() => {
    if (!open && kaart.current) { kaart.current.remove(); kaart.current = null; sectorLaag.current = null; }
  }, [open]);

  // De sectoren, gekleurd naar hun wijk.
  useEffect(() => {
    const m = kaart.current;
    if (!open || !m || !sectorenGeo?.features?.length) return;
    if (!sectorLaag.current) {
      sectorLaag.current = L.geoJSON(sectorenGeo, { pane: 'wi_wijk', interactive: false }).addTo(m);
      try { m.fitBounds(sectorLaag.current.getBounds(), { padding: [10, 10] }); }
      catch (e) { console.error('Wijk impact: kaart niet op de gemeente gezet:', e.message); }
    }
    sectorLaag.current.setStyle(f => {
      const w = f.properties?.WIJK_ID;
      const aan = w != null && String(w) === String(gekozenWijk);
      return { weight: aan ? 2.5 : 0.8, color: aan ? '#12161C' : '#9AA4B1', fillOpacity: 0.55,
               fillColor: WK[statusWijk[w] || 'geen'] };
    });
    setTimeout(() => m.invalidateSize(), 50);
  }, [open, sectorenGeo, statusWijk, gekozenWijk]);

  // Losse palen, overgenomen palen, cirkels en kandidaten.
  const cirkels = useRef([]);
  useEffect(() => {
    const m = kaart.current, g = lagen.current;
    if (!open || !m || !g) return;
    g.clearLayers();
    const overgenomen = getoond?.overgenomen || new Map();
    for (const j of jaren) {
      if (j > peiljaar) break;
      for (const p of plaatsResultaat?.perJaar?.[j]?.plekken || []) {
        if (p.soort === 'plein' || overgenomen.has(p)) continue;
        L.circleMarker([p.lat, p.lng], { pane: 'wi_los', radius: 2.5, weight: 0, fillColor: '#6B7684',
                                          fillOpacity: 0.7, interactive: false }).addTo(g);
      }
    }
    for (const [p] of overgenomen) {
      L.circleMarker([p.lat, p.lng], { pane: 'wi_los', radius: 5, weight: 0, fillColor: ORANJE, fillOpacity: 1 })
        .bindTooltip(`${wijkNaam[p.wijkId] || ''}, ${p.straat || 'straat onbekend'}: ${fmt(p.punten)} laadpunten (${p.jaar})`)
        .addTo(g);
    }
    cirkels.current = [];
    for (const l of getoond?.pleinen || []) {
      if (!((getoond.naarAndere?.[l.wijkId] || []).length)) continue;
      cirkels.current.push(L.circle([l.lat, l.lng], { pane: 'wi_cirkel', radius: loopM, color: ORANJE, weight: 3,
        dashArray: '7 5', fill: true, fillColor: ORANJE, fillOpacity: 0.08, interactive: false }).addTo(g));
    }
    for (const k of kandidaten) {
      if (k.lat == null || k.lng == null) continue;
      const isGekozen = gekozenIds.has(String(k.id));
      const isActief = String(k.id) === String(actief);
      L.circleMarker([k.lat, k.lng], {
        pane: 'wi_kand', radius: Math.max(7, Math.min(22, Math.sqrt(k.vakken || 40) * 1.1)),
        color: isActief ? '#E0218A' : isGekozen ? '#12161C' : '#fff', weight: isActief || isGekozen ? 3 : 2,
        fillColor: pleinKleur[k.status] || '#2D7F92', fillOpacity: 0.85,
      }).bindTooltip(`<b>${k.naam}</b><br>${k.wijk || ''}${k.wijk ? ' · ' : ''}klik voor de impact`)
        .on('click', () => {
          if (String(actief) === String(k.id) && !isGekozen && onKies) onKies(k.id);
          setActief(k.id); setTab('plein');
        }).addTo(g);
    }
    if (zoomNaWijziging.current) {
      zoomNaWijziging.current = false;
      try {
        if (cirkels.current.length) m.fitBounds(L.featureGroup(cirkels.current).getBounds(), { padding: [60, 60], maxZoom: 16 });
        else if (sectorLaag.current) m.fitBounds(sectorLaag.current.getBounds(), { padding: [10, 10] });
      } catch (e) { console.error('Wijk impact: inzoomen op de cirkels mislukt:', e.message); }
    }
  }, [open, getoond, plaatsResultaat, jaren, peiljaar, kandidaten, gekozenIds, actief, loopM, pleinKleur, wijkNaam, onKies]);

  if (!open) return null;

  const naarWijk = (id) => {
    setGekozenWijk(g => (String(g) === String(id) ? null : id));
    const m = kaart.current;
    if (!m || !sectorenGeo) return;
    const fs = sectorenGeo.features.filter(f => String(f.properties?.WIJK_ID) === String(id));
    if (!fs.length) return;
    try { m.fitBounds(L.geoJSON({ type: 'FeatureCollection', features: fs }).getBounds(), { padding: [40, 40] }); }
    catch (e) { console.error('Wijk impact: inzoomen op de wijk mislukt:', e.message); }
  };

  // ── Een kaartje per plein ──
  const kaartje = (loc, r, isGekozen) => {
    const pl = (r.pleinen || []).find(x => String(x.id) === String(loc.id));
    if (!pl) return null;
    const A = pl.wijkId;
    const ks = r.pleinen.filter(x => x.wijkId === A);
    const P = r.puntenWijk?.[A] || 0;
    const Y = r.eersteJaar?.[A];
    const te = Object.values(r.eigen?.[A] || {}).reduce((s, t) => s + t, 0);
    const b = r.naarAndere?.[A] || [];
    const perB = {};
    for (const x of b) perB[x.plek.wijkId] = (perB[x.plek.wijkId] || 0) + x.punten;
    const tb = b.reduce((s, x) => s + x.punten, 0);
    const over = r.restNa?.[A] || 0;
    const naam = wijkNaam[A] || 'deze wijk';
    const eerste = jarenTot.find(j => j >= Y && Math.round(jaardeel?.[A]?.[j] || 0) > 0);
    const regels = [
      [isGekozen
        ? (ks.length > 1 ? `Laadpunten op de ${ks.length} pleinen in ${naam}` : 'Laadpunten op dit plein')
        // Bij een aangeklikte kandidaat: tekst van HL, 28 oktober 2026.
        : (ks.length > 1
          ? `De laadpunten op het aangeklikte plein + de laadpunten op ${ks.length - 1} geschikt bevonden ${ks.length - 1 === 1 ? 'plein' : 'pleinen'} in ${naam}`
          : 'De laadpunten op het aangeklikte plein'), P],
      [`Voor ${naam}, tot en met ${laatsteJaar}`, te],
      [`Voor andere wijken, tot en met ${laatsteJaar}`, tb],
    ];
    if (over > 0) regels.push([`Niet nodig tot en met ${laatsteJaar}`, over]);
    const noot = eerste == null
      ? `${naam} heeft volgens tabblad Plaatsing tot en met ${laatsteJaar} geen nieuwe laadpunten nodig.`
      : eerste > Y ? `${naam} heeft volgens tabblad Plaatsing pas vanaf ${eerste} nieuwe laadpunten nodig.` : null;
    const signalen = planVoor ? planVoor(loc.lat, loc.lng) : null;
    const isActief = String(loc.id) === String(actief);
    return (
      <div key={loc.id} style={{ border: `${isActief ? 2 : 1}px solid ${isActief ? '#1F5C6B' : '#D9D5CD'}`,
        borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: '#fff' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#12161C' }}>{loc.naam}</div>
        <div style={{ color: '#6B7684', fontSize: 13.5, marginTop: 2 }}>
          {isGekozen ? 'gekozen' : 'kandidaat, nog niet gekozen'} · {fmt(pl.punten)} laadpunten
          {loc.vakken != null ? ` · ${fmt(loc.vakken)} vakken` : ''} · vanaf {pl.jaar}</div>
        <div style={st.sub}>{isGekozen ? 'Wat het plein opvangt' : 'Wat het plein zou opvangen als het gekozen wordt'}</div>
        {regels.map(([a, n], i) => (
          <React.Fragment key={a}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 15,
                          fontWeight: i === 0 ? 600 : 400, color: '#12161C' }}>
              <span>{a}</span><b>{fmt(n)}</b></div>
            {i === 2 && tb > 0 && (
              <div style={{ fontSize: 12.5, color: '#6B7684', margin: '0 0 4px' }}>
                {Object.entries(perB).sort((x, y) => y[1] - x[1]).map(([w, n]) => `${wijkNaam[w] || w} ${fmt(n)}`).join(' · ')}
              </div>
            )}
          </React.Fragment>
        ))}
        {noot && <div style={{ marginTop: 6, fontSize: 15, color: '#12161C', lineHeight: 1.45 }}>{noot}</div>}
        {signalen && signalen.regels?.length > 0 && (
          <PlanBlok signalen={signalen} plan={planMeta} kopStijl={st.sub} />
        )}
      </div>
    );
  };

  const losseKaartjes = () => {
    const lijst = [];
    if (actiefIsKandidaat && voorvertoning) lijst.push(kaartje(actiefLoc, voorvertoning, false));
    const pleinen = [...gekozen].sort((a, b) => (String(b.id) === String(actief)) - (String(a.id) === String(actief)));
    for (const l of pleinen) lijst.push(kaartje(l, impact, true));
    return lijst.filter(Boolean);
  };

  const heeftOranje = (getoond?.overgenomen?.size || 0) > 0;
  const afstanden = AFSTANDEN.includes(loopM) ? AFSTANDEN : [...AFSTANDEN, loopM].sort((a, b) => a - b);

  return (
    <div style={st.scherm} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={st.venster}>
        <div style={st.kop}>
          <b style={{ fontSize: 18, color: '#12161C' }}>Wijk impact van de laadpleinen</b>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: '#3D4652' }}>
            Peiljaar
            <select value={peiljaar} onChange={e => setPeiljaar(Number(e.target.value))} style={st.sel}>
              {jaren.map(j => <option key={j} value={j}>{j}</option>)}
            </select>
            Loopafstand
            <select value={loopM} onChange={e => { zoomNaWijziging.current = true; zetLoopM(Number(e.target.value)); }} style={st.sel}>
              {afstanden.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            m
            <span style={{ fontSize: 13.5, color: '#6B7684', marginLeft: 8 }}>
              Planperiode {beginJaar ?? jaren[0]} tot en met {laatsteJaar}, in te stellen in Parameters</span>
          </span>
          <span onClick={onClose} style={{ marginLeft: 'auto', fontSize: 24, cursor: 'pointer', color: '#6B7684' }}>&times;</span>
        </div>
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1.45, position: 'relative', borderRight: '1px solid #E5E1DA' }}>
            <div ref={kaartDiv} style={{ position: 'absolute', inset: 0 }} />
            <div style={st.legenda}>
              {Object.keys(WK).map(k => (
                <span key={k} style={st.legItem}>
                  <span style={{ width: 12, height: 12, background: WK[k], border: '1px solid #9AA4B1', display: 'inline-block' }} />
                  {WN[k]}</span>
              ))}
              {heeftOranje && <>
                <span style={st.legItem}><span style={{ width: 9, height: 9, borderRadius: '50%', background: ORANJE, display: 'inline-block' }} />
                  los laadpunt dat vervalt: een laadplein binnen {fmt(loopM)} m neemt het over</span>
                <span style={st.legItem}><span style={{ width: 12, height: 12, borderRadius: '50%', border: `2px dashed ${ORANJE}`, display: 'inline-block' }} />
                  {fmt(loopM)} m rond dat laadplein</span>
              </>}
              <span style={st.legItem}><span style={{ width: 7, height: 7, borderRadius: '50%', background: '#6B7684', display: 'inline-block' }} />
                los laadpunt</span>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', background: '#FAF9F6' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[['wijk', 'Per wijk'], ['plein', 'Per plein']].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} style={st.tab(tab === id)}>{label}</button>
              ))}
            </div>
            {!impact && <div style={{ fontSize: 15, color: '#6B7684' }}>De plaatsing is nog niet berekend.</div>}
            {impact && tab === 'wijk' && (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...st.th, textAlign: 'left' }}>Wijk</th>
                    <th style={st.th}>Nieuwe laadpunten<br />tot {peiljaar}</th>
                    <th style={st.th}>Via<br />laadpleinen</th>
                    <th style={st.th}>Losse<br />laadpunten</th>
                  </tr></thead>
                  <tbody>
                    {rijen.map(x => (
                      <tr key={x.id} onClick={() => naarWijk(x.id)}
                        style={{ cursor: 'pointer', background: String(x.id) === String(gekozenWijk) ? '#EEF4F6' : 'transparent' }}>
                        <td style={{ ...st.td, textAlign: 'left' }}>
                          <span style={{ width: 11, height: 11, background: WK[x.st], border: '1px solid #9AA4B1',
                                         display: 'inline-block', marginRight: 7, verticalAlign: -1 }} />{x.naam}</td>
                        <td style={st.td}>{fmt(x.n)}</td>
                        <td style={st.td}>{x.v ? fmt(x.v) : ''}</td>
                        <td style={st.td}>{fmt(x.los)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={st.voet}>Nieuwe laadpunten = via laadpleinen + losse laadpunten.</div>
              </>
            )}
            {impact && tab === 'plein' && (
              <>
                {actiefIsKandidaat && (
                  <div style={{ ...st.voet, margin: '0 0 8px' }}>Klik nog een keer op de bol om deze kandidaat te kiezen.</div>
                )}
                {(() => {
                  const k = losseKaartjes();
                  return k.length ? k : <div style={{ fontSize: 15, color: '#6B7684' }}>Nog geen locatie op geschikt.</div>;
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const st = {
  scherm: { position: 'fixed', inset: 0, background: 'rgba(18,22,28,.45)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center' },
  venster: { width: 'min(1500px,96vw)', height: 'min(900px,92vh)', background: '#fff', borderRadius: 12,
             display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(18,22,28,.3)' },
  kop: { display: 'flex', alignItems: 'center', gap: 18, padding: '12px 18px', borderBottom: '1px solid #E5E1DA' },
  sel: { fontSize: 15, padding: '3px 6px', border: '1px solid #C4BFB5', borderRadius: 6, fontFamily: 'inherit' },
  legenda: { position: 'absolute', left: 10, bottom: 10, zIndex: 500, background: '#fff', border: '1px solid #C4BFB5',
             borderRadius: 7, padding: '7px 10px', fontSize: 12.5, color: '#3D4652', display: 'flex', gap: 12,
             flexWrap: 'wrap', maxWidth: 'calc(100% - 20px)' },
  legItem: { display: 'flex', alignItems: 'center', gap: 5 },
  tab: (aan) => ({ fontSize: 14.5, padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                   border: `1px solid ${aan ? '#1F5C6B' : '#C4BFB5'}`, background: aan ? '#1F5C6B' : '#fff',
                   color: aan ? '#fff' : '#3D4652', fontWeight: aan ? 700 : 400 }),
  th: { fontSize: 13, color: '#6B7684', fontWeight: 600, textAlign: 'right', padding: '6px 6px',
        borderBottom: '1px solid #D9D5CD', verticalAlign: 'bottom' },
  td: { fontSize: 15, color: '#12161C', textAlign: 'right', padding: '6px 6px', borderBottom: '1px solid #EEEAE3' },
  voet: { fontSize: 12.5, color: '#6B7684', marginTop: 8, lineHeight: 1.45 },
  sub: { fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#6B7684',
         margin: '12px 0 5px' },
};
